import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faImage, faPhotoFilm} from '@fortawesome/free-solid-svg-icons';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { aiStateTypes} from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ImageGernerationSimpleInputProps } from '../../types/imageSimple/imageGeneration';
import { ImageGenerationBodyProps, samplerList } from '../../types/ai/api';
import { handleGetRandomNumber } from '../../utils/calculations';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';
import { getImagePixelSize } from '../../utils/images/imageConvertFormats';
import { useUpdateImageGenerationCosts } from '../../hooks/useUpdateImageGenerationCosts';
import { parsePromptResponse } from '../../utils/chat/chatPropsParsing';
import { GenerationFormatType, usePromptHook } from '../../hooks/usePromptHook';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { SettingAIOptions } from '../../components/app/generation/SettingAIOptions';
import { FilePlaceHolderWithDragAndDrop } from '../../components/fileupload/filePlaceholderWithDragAndDrop';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const ImageToGifGeneration = () => {
    const { t } = useTranslation(['generation','toolsApps']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [image,setImage] = useState<{file:any,src:string}|null>(null);
    const [generateGif, setGenerateGif] = useState(false);
    const [inputsImgGeneration,setInputsImgGeneration] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)',
        numberOgGeneration: 1,
        reference: null
    });
    const [seedRandomActive, setSeedRandomActive] = useState(true);
    const [inputsGifGeneration, setInputsGifGeneration] = useState('');
    const [generationIMG,stateIMG,setStateIMG,getJobIDIMG,insufficientCreditsDialogIMG] = useAiImageTypesGeneration();
    const [generationGIF,stateGIF,setStateGIF,getJobIDGIF,insufficientCreditsDialogGIF] = useAiImageTypesGeneration();
    const [generationLLM,stateLLM,setStateLLM,handleRandomPromptExternal,handleCheckPromptExternal, _,insufficientCreditsDialogLLM] = usePromptHook();
    const [aiState, setAiState] = useState<aiStateTypes>(null);
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);
    const [gif, setGif] = useState<string|null>(null);
    const [creationCosts, updateCosts] = useUpdateImageGenerationCosts();

    const handleRandomPrompt = async (format: GenerationFormatType) => {
        setStateIMG(null);
        setStateGIF(null);
        setAiGenerationIsRunning(true);
        if(format === 'gif'){
            setGenerateGif(true);
        }
        await handleRandomPromptExternal('create',true); 
    };
    const handleCheckPrompt = async (format: GenerationFormatType) => {
        setStateIMG(null);
        setStateGIF(null);
        setAiGenerationIsRunning(true);
        if(format === 'gif'){
            setGenerateGif(true);
        }
        await handleCheckPromptExternal(inputsImgGeneration.promptPositive,inputsImgGeneration.promptNegative, 'create');
    };
    const handleGenerationResultImage = async() => {
        setAiGenerationIsRunning(false);
        let img = generationIMG[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setImage({
            file:imgFile,
            src:img
        });
        if(seedRandomActive){
            const currentInputs = {...inputsImgGeneration};
            currentInputs.seed = handleGetRandomNumber();
            setInputsImgGeneration(currentInputs);
        }
    };
    const handleGenerationResultGIF = () => {
        setAiGenerationIsRunning(false);
        let gif = generationGIF[0];
        setGif(gif);
    };
    const generateImage = async() => {
        setStateIMG(null);
        setStateGIF(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        //request to get job id
        const imageDimension = getImagePixelSize(inputsImgGeneration.aspectRatio, inputsImgGeneration.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputsImgGeneration.promptPositive,inputsImgGeneration.promptNegative, inputsImgGeneration.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsImgGeneration.seed,
            steps: inputsImgGeneration.samplingSteps,
            scheduler: inputsImgGeneration.scheduler,
            model: 'Qwen_Image',
            cfg: inputsImgGeneration.cfg,
            number_of_images: 1,
            sampler: inputsImgGeneration.sampler
        };
        setStateIMG('send');
        let path = 'generate/image'
        try {
            getJobIDIMG('image_gen',path,body);
        } catch(err){
            setStateIMG('error');
            setAiGenerationIsRunning(false);
        };
    };
    const generateGifFromImage = async () => {
        setGenerateGif(true);
        setStateGIF(null);
        setStateIMG(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        const prompt = inputsGifGeneration;
        if(!image){
            setStateGIF('error');
            setAiGenerationIsRunning(false);
            return;
        }
        const file = image.file;
        const base64Image = await fileToBase64(file);
        const body = {
            "positive_prompt": prompt,
            "image": base64Image
        };
        try {
            setStateGIF('send');
            let path = 'generate/image-to-gif'
            getJobIDGIF('image_gen',path,body);
        } catch(err){
            setStateGIF('error');
            setAiGenerationIsRunning(false);
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputsImgGeneration(prev => ({...prev,[name]: value}));
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setImage({
                'file':file,
                'src':imgSrc
            });
        }
    };
    const handleDroppedImage = (file: File) => {
        if (!file.type.startsWith("image/")) return;

        const src = URL.createObjectURL(file);
        setImage({ file, src });
    };
    const download = (imageDownload:boolean) => {
        let resource = '';
        if (imageDownload && image){
            resource = image.src;
        }
        else if (gif) {
            resource = gif;
        }
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.imageToGif.name')
    },[]);
    useEffect(()=>{
        updateCosts(inputsImgGeneration);
    },[inputsImgGeneration]);
    useEffect(()=>{
        if(generationIMG.length > 0){
            handleGenerationResultImage();
        }
    },[generationIMG]);
    useEffect(()=>{
        if(generationGIF.length > 0){
            handleGenerationResultGIF();
        }
    },[generationGIF]);
    useEffect(() => {
        setAiGenerationIsRunning(false);
        setStateLLM(null);
        if (!generationLLM) return;
        const promptResponse = parsePromptResponse(generationLLM);
        if (!promptResponse) return;
        
        if(generateGif){
            setGenerateGif(false);
            setInputsGifGeneration(generationLLM.positive_prompt);
        }
        else{
            setInputsImgGeneration(prev => ({
                ...prev,
                prompt_positive: promptResponse.positive_prompt,
                prompt_negative: promptResponse.negative_prompt ?? ""
            }));
        }
    }, [generationLLM]);
    useEffect(()=>{
        setGenerateGif(false);
    },[image]);

    useEffect(()=>{
        if(stateIMG === 'done' || stateIMG === 'error' || stateIMG === 'unavailable' || stateLLM === 'error' || stateLLM === 'unavailable' || stateLLM === 'done' || stateGIF === 'error' || stateGIF === 'unavailable' || stateGIF === 'done'){
            setAiGenerationIsRunning(false);
        }
        if(stateIMG !== null){
            setStateLLM(null);
            setStateGIF(null);
            setAiState(stateIMG);
        }
        if(stateLLM !== null){
            setStateIMG(null);
            setStateGIF(null);
            setAiState(stateLLM);
        }
        if(stateGIF !== null){
            setStateIMG(null);
            setStateLLM(null);
        }
        if(!stateIMG && !stateLLM){
            setAiState(null);
        }
    },[stateIMG, stateGIF,stateLLM]);

  return (
    <>
        {insufficientCreditsDialogIMG}
        {insufficientCreditsDialogGIF}
        {insufficientCreditsDialogLLM}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button 
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title="Back to executions"
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('generateGif')}
                    </div>
                    <div className='col'/>
                </div>
                <div className={`row flex-grow-1 p-2 m-0 ${isMobile ? "overflow-y-auto overflow-x-hidden" : ""}`} style={{ minHeight: 0 }}>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 1
                            </div>
                            <div className='col-auto h4'>
                                {t('generateImage')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={aiState} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: image ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ image ? image.src : null}
                                imageAltText={t('lastGeneratedImage')}
                                onDownload={() => download(true)}
                                donwloadTitle={t("download.image")}
                                onDropImage={handleDroppedImage}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={creationCosts ?? 0}
                                onCheckPrompt={() => handleCheckPrompt('image')}
                                onRandomPrompt={() => handleRandomPrompt('image')}
                                onGenerateImage={generateImage}
                                promptNegative={inputsImgGeneration.promptNegative ?? ''}
                                promptPositive={inputsImgGeneration.promptPositive ?? ''}
                                onInputChange={handleInputChange}
                                onUploadFile={uploadFile}
                            />
                            {   isMobile && (
                                    <SettingAIOptions
                                        aspectRatio={inputsImgGeneration.aspectRatio}
                                        style={inputsImgGeneration.style}
                                        sampler={inputsImgGeneration.sampler}
                                        samplingSteps={inputsImgGeneration.samplingSteps}
                                        seed={inputsImgGeneration.seed}
                                        seedRandomActive={seedRandomActive}
                                        setSeedRandomActive={setSeedRandomActive}
                                        cfg={inputsImgGeneration.cfg}
                                        scheduler={inputsImgGeneration.scheduler}
                                        quality={inputsImgGeneration.quality}
                                        onInputsChange={setInputsImgGeneration}
                                    />
                                )
                            }
                        </div>
                    </div>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 2
                            </div>
                            <div className='col-auto h4'>
                                {t('generateGif')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={stateGIF} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: gif ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ gif ?? null}
                                imageAltText={t('lastGeneratedGif')}
                                onDownload={() => download(false)}
                                donwloadTitle={t("gif.downloadGif")}
                                placeholderIcon={faPhotoFilm}
                                placeholderText={t('noGeneratedGif')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={0}
                                onGenerateImage={generateGifFromImage}
                                promptPositive={inputsGifGeneration}
                                onInputChange={(e) => setInputsGifGeneration(e.target.value)}
                                placeholderPromptPositive={t('placeholder.animate')}
                                onCheckPrompt={() => handleCheckPrompt('gif')}
                                onRandomPrompt={() => handleRandomPrompt('gif')}
                            />
                        </div>
                    </div>
                </div>
                {   !isMobile && (
                        <div className='row mx-0'>
                            <div className='col-6'>
                                <SettingAIOptions
                                    aspectRatio={inputsImgGeneration.aspectRatio}
                                    style={inputsImgGeneration.style}
                                    sampler={inputsImgGeneration.sampler}
                                    samplingSteps={inputsImgGeneration.samplingSteps}
                                    seed={inputsImgGeneration.seed}
                                    seedRandomActive={seedRandomActive}
                                    setSeedRandomActive={setSeedRandomActive}
                                    cfg={inputsImgGeneration.cfg}
                                    scheduler={inputsImgGeneration.scheduler}
                                    quality={inputsImgGeneration.quality}
                                    onInputsChange={setInputsImgGeneration}
                                />
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    </>
  );
};

export default ImageToGifGeneration;